/**
 * Reminder lead-time policy (pure). Fixed product defaults:
 * - Start reminder: 30 minutes before the scheduled start.
 * - End reminder: 15 minutes before the scheduled end.
 *
 * These are the single source of truth for the backend reminder scheduler.
 */

import type { Session } from './types'

export const REMINDER_BEFORE_START_MS = 30 * 60 * 1000
export const REMINDER_BEFORE_END_MS = 15 * 60 * 1000
/**
 * The end reminder never fires at or before the scheduled start. A 15-minute
 * session's nominal T−15 would land exactly AT the start ("remember to record
 * your departure" before the provider has even begun), so the end reminder is
 * held until this long after the start. A session too short to have a
 * meaningful reminder window (fire time would be at/after its end) gets none.
 */
export const MIN_END_REMINDER_AFTER_START_MS = 5 * 60 * 1000
/**
 * How long after the scheduled end a never-started session is marked
 * "Not started" (owner decision 2026-10-08). No arrival recorded, not
 * cancelled, nothing written to AHP.
 */
export const NOT_STARTED_AFTER_END_MS = 2 * 60 * 60 * 1000

/**
 * Minimum useful lead time for the pre-session warning. A start reminder that
 * can no longer be delivered at least this long before the session start is
 * dropped (claimed but not sent) — a "do your pre-session checks" push 2
 * minutes before (or after) the start is noise, not a warning.
 *
 * This replaces the old fixed 5-minute "stale" window for the START reminder
 * only: a session BOOKED closer than 30 minutes to its start (short-notice)
 * now still gets one immediate warning with an honest countdown, instead of
 * being silently skipped because the nominal T−30 fire time was already in
 * the past when the booking landed.
 */
export const MIN_START_REMINDER_LEAD_MS = 5 * 60 * 1000

/**
 * Whole minutes until `startMs`, rounded for push copy ("about N minutes").
 * The scheduler runs on a 5-minute EventBridge cadence, so the nominal T−30
 * reminder actually sends at T−30…T−25. Values ≥ 15 minutes round to the
 * nearest 5 so the copy says "about 30 minutes" rather than "29"; shorter
 * (short-notice) leads stay exact so the push never claims more time than the
 * provider really has. Never returns less than 1.
 */
export function approxMinutesUntil(startMs: number, now: number): number {
  const raw = Math.max(1, Math.round((startMs - now) / 60_000))
  if (raw < 15) return raw
  return Math.max(15, Math.round(raw / 5) * 5)
}

export type ReminderKind = 'start' | 'end' | 'notStarted'

export interface ReminderTime {
  kind: ReminderKind
  /** When the reminder should fire, epoch ms (UTC). */
  fireAt: number
}

/**
 * Compute the start/end/not-started reminder fire times for a session. The
 * `end` entry is omitted for a session too short to carry one (see
 * {@link MIN_END_REMINDER_AFTER_START_MS}).
 */
export function computeReminderTimes(
  session: Pick<Session, 'start' | 'end'>,
): ReminderTime[] {
  const times: ReminderTime[] = [
    { kind: 'start', fireAt: session.start - REMINDER_BEFORE_START_MS },
  ]
  const endFireAt = Math.max(
    session.end - REMINDER_BEFORE_END_MS,
    session.start + MIN_END_REMINDER_AFTER_START_MS,
  )
  if (endFireAt < session.end) times.push({ kind: 'end', fireAt: endFireAt })
  times.push({ kind: 'notStarted', fireAt: session.end + NOT_STARTED_AFTER_END_MS })
  return times
}

/**
 * Which reminders have already been delivered. The backend persists one
 * fire-once timestamp per kind (`startReminderSentAt` / `endReminderSentAt`);
 * a non-null value means that reminder was already sent.
 */
export interface ReminderSentState {
  startReminderSentAt?: number | null
  endReminderSentAt?: number | null
  notStartedReminderSentAt?: number | null
}

/** True when the reminder of `kind` has already been delivered. */
export function isReminderSent(kind: ReminderKind, sent: ReminderSentState): boolean {
  if (kind === 'start') return sent.startReminderSentAt != null
  if (kind === 'end') return sent.endReminderSentAt != null
  return sent.notStartedReminderSentAt != null
}

/**
 * The earliest reminder that has NOT yet been sent, or `null` when start, end,
 * and the not-started mark have all fired. This is the single source of truth
 * for the "due reminder" index sort key (`reminderDueAt`) the backend
 * maintains so the scheduler can QUERY for due reminders instead of scanning
 * the whole table.
 */
export function nextUnsentReminder(
  session: Pick<Session, 'start' | 'end'>,
  sent: ReminderSentState,
): ReminderTime | null {
  const pending = computeReminderTimes(session).filter((t) => !isReminderSent(t.kind, sent))
  if (pending.length === 0) return null
  return pending.reduce((earliest, t) => (t.fireAt < earliest.fireAt ? t : earliest))
}

/**
 * Which reminders are due at `now` (with an optional look-ahead window in ms).
 * A reminder is "due" once `now >= fireAt` (and, when a window is given, only
 * within `windowMs` after `fireAt` so a scan doesn't resurface stale reminders).
 */
export function dueReminders(
  session: Pick<Session, 'start' | 'end'>,
  now: number,
  windowMs?: number,
): ReminderTime[] {
  return computeReminderTimes(session).filter(({ fireAt }) => {
    if (now < fireAt) return false
    if (windowMs === undefined) return true
    return now - fireAt <= windowMs
  })
}
